import type { AuthSession, AuthUser } from "../types/auth.types";
import {
  clearAuthTokenCookie,
  readAuthTokenCookie,
} from "./auth-cookie";

const AUTH_SYNC_STORAGE_KEY = "b2c_auth_sync_event";
const AUTH_SYNC_CHANNEL_NAME = "b2c_auth_sync_channel";

export type AuthSyncEventType = "login" | "logout";

export type AuthSyncEvent = {
  id: string;
  type: AuthSyncEventType;
  source: string;
  timestamp: number;
};

let authSyncSourceId: string | null = null;

export function getAuthSyncSourceId() {
  if (authSyncSourceId) {
    return authSyncSourceId;
  }

  authSyncSourceId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return authSyncSourceId;
}

function extractString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function extractObject(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function extractBooleanish(value: unknown): boolean | null {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
    return null;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["1", "true", "yes", "on", "enabled"].includes(normalized)) {
      return true;
    }
    if (["0", "false", "no", "off", "disabled"].includes(normalized)) {
      return false;
    }
  }

  return null;
}

function extractNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function resolveToken(payload: unknown): string | null {
  const topLevel = extractObject(payload);
  const nestedData = extractObject(topLevel?.data);

  return (
    extractString(topLevel?.token) ??
    extractString(topLevel?.access_token) ??
    extractString(topLevel?.accessToken) ??
    extractString(nestedData?.token) ??
    extractString(nestedData?.access_token) ??
    extractString(nestedData?.accessToken) ??
    null
  );
}

function resolveUser(payload: unknown): AuthUser | null {
  const topLevel = extractObject(payload);
  const nestedData = extractObject(topLevel?.data);

  const hasUserShape = (value: Record<string, unknown> | null) => {
    if (!value) return false;
    return (
      "id" in value ||
      "user_id" in value ||
      "name" in value ||
      "email" in value ||
      "phone_number" in value
    );
  };

  const user =
    extractObject(topLevel?.profile) ??
    extractObject(topLevel?.user) ??
    extractObject(nestedData?.user) ??
    extractObject(nestedData?.profile) ??
    (hasUserShape(nestedData) ? nestedData : null) ??
    null;

  return user as AuthUser | null;
}

export function extractResponseMessage(payload: unknown): string | null {
  const topLevel = extractObject(payload);
  const nestedData = extractObject(topLevel?.data);

  return (
    extractString(topLevel?.message) ?? extractString(nestedData?.message) ?? null
  );
}

export function isTwoFactorRequired(payload: unknown) {
  const topLevel = extractObject(payload);
  const nestedData = extractObject(topLevel?.data);

  return Boolean(
    extractBooleanish(topLevel?.requires_2fa) ??
      extractBooleanish(topLevel?.is_2fa_required) ??
      extractBooleanish(topLevel?.two_fa_required) ??
      extractBooleanish(topLevel?.two_fa) ??
      extractBooleanish(nestedData?.requires_2fa) ??
      extractBooleanish(nestedData?.is_2fa_required) ??
      extractBooleanish(nestedData?.two_fa_required) ??
      extractBooleanish(nestedData?.two_fa),
  );
}

export function extractTwoFactorUserOrEmail(payload: unknown): string | null {
  const topLevel = extractObject(payload);
  const nestedData = extractObject(topLevel?.data);
  const nestedUser = extractObject(nestedData?.user);

  return (
    extractString(topLevel?.user_or_email) ??
    extractString(topLevel?.email) ??
    extractString(nestedData?.user_or_email) ??
    extractString(nestedData?.email) ??
    extractString(nestedUser?.email) ??
    extractString(nestedUser?.username) ??
    null
  );
}

export function createSessionFromResponse(payload: unknown): AuthSession | null {
  const accessToken = resolveToken(payload);
  if (!accessToken) {
    return null;
  }

  return {
    accessToken,
    user: resolveUser(payload),
  };
}

export function extractTokenFromResponse(payload: unknown) {
  return resolveToken(payload);
}

export function extractUserFromResponse(payload: unknown) {
  return resolveUser(payload);
}

function parseAuthSyncEvent(payload: unknown): AuthSyncEvent | null {
  const raw = extractObject(payload);
  const id = extractString(raw?.id);
  const source = extractString(raw?.source);
  const type = extractString(raw?.type);
  const timestamp = extractNumber(raw?.timestamp);

  if (!id || !source || !timestamp) {
    return null;
  }

  if (type !== "login" && type !== "logout") {
    return null;
  }

  return {
    id,
    source,
    type,
    timestamp,
  };
}

function emitAuthSyncEvent(type: AuthSyncEventType) {
  if (typeof window === "undefined") {
    return;
  }

  const event: AuthSyncEvent = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    type,
    source: getAuthSyncSourceId(),
    timestamp: Date.now(),
  };

  try {
    window.localStorage.setItem(AUTH_SYNC_STORAGE_KEY, JSON.stringify(event));
  } catch {
    // Ignore storage write failures in private mode or blocked storage.
  }

  try {
    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel(AUTH_SYNC_CHANNEL_NAME);
      channel.postMessage(event);
      channel.close();
    }
  } catch {
    // Ignore BroadcastChannel failures in unsupported/locked-down environments.
  }
}

export function subscribeToAuthSync(onEvent: (event: AuthSyncEvent) => void) {
  if (typeof window === "undefined") {
    return () => {};
  }

  let channel: BroadcastChannel | null = null;
  let lastEventId: string | null = null;

  const handleEventPayload = (payload: unknown) => {
    const parsed = parseAuthSyncEvent(payload);
    if (!parsed) {
      return;
    }

    if (parsed.id === lastEventId) {
      return;
    }
    lastEventId = parsed.id;

    onEvent(parsed);
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== AUTH_SYNC_STORAGE_KEY || !event.newValue) {
      return;
    }

    try {
      handleEventPayload(JSON.parse(event.newValue));
    } catch {
      // Ignore invalid payloads from storage.
    }
  };

  window.addEventListener("storage", handleStorage);

  if (typeof BroadcastChannel !== "undefined") {
    channel = new BroadcastChannel(AUTH_SYNC_CHANNEL_NAME);
    channel.onmessage = (event) => {
      handleEventPayload(event.data);
    };
  }

  return () => {
    window.removeEventListener("storage", handleStorage);
    if (channel) {
      channel.close();
    }
  };
}

export async function persistAuthSession() {
  const accessToken = await readAuthTokenCookie();
  if (!accessToken) {
    return false;
  }

  emitAuthSyncEvent("login");
  return true;
}

export async function readPersistedAuthSession(): Promise<AuthSession | null> {
  const accessToken = await readAuthTokenCookie();
  if (!accessToken) {
    return null;
  }

  return {
    accessToken,
    user: null,
  };
}

export async function clearPersistedAuthSession() {
  await clearAuthTokenCookie();
  emitAuthSyncEvent("logout");
}
